import { supabase } from './supabase'

// ── Types ──────────────────────────────────────────────────────────────────

export type SubscriptionPlan = 'free' | 'basic' | 'pro' | 'premium'
export type SubscriptionStatus = 'active' | 'expired' | 'cancelled'

export interface Subscription {
    id: string
    seller_id: string
    plan: SubscriptionPlan | string // Allow string for mapping
    status: SubscriptionStatus
    paystack_ref: string | null
    amount_paid: number | null
    started_at: string
    expires_at: string
    created_at: string
    cancel_at_period_end?: boolean
    cancelled_at?: string | null
}

// Pricing in kobo (₦ × 100)
export const PLANS = {
    basic: {
        label: 'Monthly',
        amount: 100_000,         // ₦1,000 in kobo
        amountLabel: '₦1,000',
        perLabel: '/month',
        durationDays: 30,
        planCode: import.meta.env.VITE_PAYSTACK_PLAN_CODE_BASIC,
    },
    pro: {
        label: 'Yearly',
        amount: 1_000_000,       // ₦10,000 in kobo
        amountLabel: '₦10,000',
        perLabel: '/year',
        durationDays: 365,
    },
} as const

// ── Queries ────────────────────────────────────────────────────────────────

/** Fetch the most recent active subscription for the current seller */
export async function fetchActiveSubscription(): Promise<{
    data: Subscription | null
    error: string | null
}> {
    const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('status', 'active')
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false })
        .limit(1)
        .maybeSingle()

    if (error) return { data: null, error: error.message }
    return { data: data as Subscription | null, error: null }
}

/** Fetch all subscriptions (payments) for a seller */
export async function fetchSubscriptionHistory(): Promise<{
    data: Subscription[] | null
    error: string | null
}> {
    const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10) // Last 10 payments covers ~3 months easily

    if (error) return { data: null, error: error.message }
    return { data: data as Subscription[], error: null }
}

/** Cancel a subscription (sets it to expire without renewal) */
export async function cancelSubscription(subscriptionId: string): Promise<{ error: string | null }> {
    const { error } = await supabase
        .from('subscriptions')
        .update({ 
            status: 'cancelled',
            cancel_at_period_end: true,
            cancelled_at: new Date().toISOString()
        })
        .eq('id', subscriptionId)

    if (error) return { error: error.message }
    return { error: null }
}


/** 
 * Verifies a transaction via Supabase Edge Function (Server-side)
 * This is the secure way to activate subscriptions.
 */
export async function verifySubscription(params: {
    reference: string
    plan: SubscriptionPlan
    sellerId: string
}): Promise<{ error: string | null }> {
    const { data, error } = await supabase.functions.invoke('verify-subscription', {
        body: {
            reference: params.reference,
            plan: params.plan,
            sellerId: params.sellerId
        }
    })

    if (error) return { error: error.message }
    if (data?.error) return { error: data.error }

    return { error: null }
}

/** Open the Paystack payment popup */
export function openPaystackPopup(params: {
    email: string
    amount: number        // in kobo
    plan: SubscriptionPlan
    onSuccess: (reference: string) => void
    onClose: () => void
}) {
    const publicKey = (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string)?.trim()

    if (!publicKey) {
        console.error('[MoniFlow] VITE_PAYSTACK_PUBLIC_KEY is not set')
        return
    }

    // @ts-expect-error — PaystackPop is loaded from inline-js
    const handler = (window.PaystackPop ?? globalThis.PaystackPop)?.setup({
        key: publicKey,
        email: params.email,
        amount: params.amount,
        currency: 'NGN',
        plan: params.plan === 'basic' ? PLANS.basic.planCode : undefined,
        ref: `moniflow_${params.plan}_${Date.now()}`,
        metadata: {
            custom_fields: [
                { display_name: 'Plan', variable_name: 'plan', value: params.plan },
            ],
        },
        callback: (response: { reference: string }) => {
            params.onSuccess(response.reference)
        },
        onClose: params.onClose,
    })

    handler?.openIframe()
}

/** Open the Paystack payment popup automatically using a pre-generated access_code */
export function openPaystackWithAccessCode(params: {
    accessCode: string
    onSuccess: (reference: string) => void
    onClose: () => void
}) {
    const publicKey = (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string)?.trim()

    if (!publicKey) {
        console.error('[MoniFlow] VITE_PAYSTACK_PUBLIC_KEY is not set')
        return
    }

    // @ts-expect-error — PaystackPop is loaded from inline-js
    const handler = (window.PaystackPop ?? globalThis.PaystackPop)?.setup({
        key: publicKey,
        access_code: params.accessCode,
        callback: (response: { reference: string }) => {
            params.onSuccess(response.reference)
        },
        onClose: params.onClose,
    })

    handler?.openIframe()
}
